import {
  Button,
  Checkbox,
  CheckboxGroup,
  Chip,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import {
  ArrowsClockwise,
  CheckSquare,
  FunnelSimple,
  MagnifyingGlass,
  Printer,
  Tag,
  X,
} from "@phosphor-icons/react";
import { Dispatch, SetStateAction, useCallback } from "react";
import type {
  BuildingRow,
  LabelCountMap,
  StatusFilterOption,
} from "./types";
import { AVAILABLE_STATUSES } from "./types";

interface Props {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  isSearchFocused: boolean;
  setIsSearchFocused: Dispatch<SetStateAction<boolean>>;

  filteredBuildings: BuildingRow[];
  selectedBuildings: Set<string>;
  setSelectedBuildings: Dispatch<SetStateAction<Set<string>>>;
  onClearAllBuildings: () => void;
  onRemoveBuilding: (id: string) => void;

  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;

  currentSection: "buildings" | "units" | null;
  scrollToBuildings: () => void;
  scrollToUnits: () => void;

  availableBuildingLabels: string[];
  availableUnitLabels: string[];
  selectedBuildingLabels: Set<string>;
  selectedUnitLabels: Set<string>;
  toggleBuildingLabel: (label: string, e?: React.MouseEvent) => void;
  toggleUnitLabel: (label: string, e?: React.MouseEvent) => void;
  selectAllBuildingLabels: (e?: React.MouseEvent) => void;
  deselectAllBuildingLabels: (e?: React.MouseEvent) => void;
  selectAllUnitLabels: (e?: React.MouseEvent) => void;
  deselectAllUnitLabels: (e?: React.MouseEvent) => void;

  buildingLabelsExpanded: boolean;
  setBuildingLabelsExpanded: Dispatch<SetStateAction<boolean>>;
  unitLabelsExpanded: boolean;
  setUnitLabelsExpanded: Dispatch<SetStateAction<boolean>>;

  buildingLabelCounts: LabelCountMap;
  unitLabelCounts: LabelCountMap;

  refreshing: boolean;
  onRefresh: () => void;
  onPrint: () => void;
}

const StatusFilter = ({
  selectedStatuses,
  setSelectedStatuses,
  options,
}: {
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  options: StatusFilterOption[];
}) => (
  <Popover placement="top">
    <PopoverTrigger>
      <Button
        startContent={<FunnelSimple size={16} />}
        className={`px-3 ${
          selectedStatuses.length > 0
            ? "bg-moss-primary text-white"
            : "border-neutral-primary border bg-white"
        }`}
        size="sm"
      >
        Status filter
        {selectedStatuses.length > 0 && ` (${selectedStatuses.length})`}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-72 p-2">
      <div className="px-1 py-2">
        <p className="mb-2 text-sm font-semibold">Filter op status</p>
        <div className="divide-y">
          <div className="py-2">
            <CheckboxGroup
              value={selectedStatuses}
              onValueChange={setSelectedStatuses}
              orientation="vertical"
              className="gap-1"
            >
              {options.map((status) => (
                <Checkbox
                  key={status.key}
                  value={status.key}
                  classNames={{ base: "max-w-full", label: "w-full" }}
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-4 w-4 rounded-xs ${status.colorClass}`} />
                    <span>{status.label}</span>
                  </div>
                </Checkbox>
              ))}
            </CheckboxGroup>
          </div>
          <div className="flex justify-between py-2">
            <Button
              size="sm"
              className="text-xs"
              onClick={() => setSelectedStatuses(options.map((s) => s.key))}
            >
              Selecteer alles
            </Button>
            <Button
              size="sm"
              className="text-xs"
              onClick={() => setSelectedStatuses([])}
            >
              Wis selectie
            </Button>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
);

export const DueDiligenceStickyControls = ({
  searchTerm,
  setSearchTerm,
  isSearchFocused,
  setIsSearchFocused,
  filteredBuildings,
  selectedBuildings,
  setSelectedBuildings,
  onClearAllBuildings,
  onRemoveBuilding,
  selectedStatuses,
  setSelectedStatuses,
  currentSection,
  scrollToBuildings,
  scrollToUnits,
  availableBuildingLabels,
  availableUnitLabels,
  selectedBuildingLabels,
  selectedUnitLabels,
  toggleBuildingLabel,
  toggleUnitLabel,
  selectAllBuildingLabels,
  deselectAllBuildingLabels,
  selectAllUnitLabels,
  deselectAllUnitLabels,
  buildingLabelsExpanded,
  setBuildingLabelsExpanded,
  unitLabelsExpanded,
  setUnitLabelsExpanded,
  buildingLabelCounts,
  unitLabelCounts,
  refreshing,
  onRefresh,
  onPrint,
}: Props) => {
  const handleBuildingSuggestionClick = useCallback(
    (id: string) => {
      setSelectedBuildings((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [setSelectedBuildings],
  );

  return (
    <div className="border-moss-primary/20 fixed bottom-0 left-0 z-30 w-full border-t-2 bg-white/95 px-4 py-3 shadow-2xl backdrop-blur-sm">
      <div className="container mx-auto flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Input
                placeholder="Zoeken..."
                startContent={<MagnifyingGlass size={16} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                className="w-[300px]"
                size="sm"
              />

              {isSearchFocused && (
                <div className="border-neutral-secondary-mid absolute right-0 bottom-full left-0 z-40 mb-1 max-h-[200px] overflow-y-auto rounded-md border bg-white shadow-lg">
                  <div className="border-neutral-secondary-mid border-b p-2 text-sm font-semibold">
                    Beschikbare gebouwen:
                  </div>
                  {filteredBuildings.length > 0 ? (
                    <div>
                      {filteredBuildings.slice(0, 7).map((building) => (
                        <div
                          key={building.id}
                          className="hover:bg-moss-primary/10 flex cursor-pointer items-center justify-between p-2 text-sm"
                          onClick={() =>
                            handleBuildingSuggestionClick(building.id)
                          }
                        >
                          <div>
                            <div className="font-medium">{building.address}</div>
                            <div className="text-neutral-secondary-mid text-xs">
                              {building.id}
                            </div>
                          </div>
                          {selectedBuildings.has(building.id) && (
                            <CheckSquare
                              size={16}
                              className="text-moss-primary"
                              weight="fill"
                            />
                          )}
                        </div>
                      ))}
                      {filteredBuildings.length > 7 && (
                        <div className="text-neutral-secondary-mid border-neutral-secondary-mid border-t p-2 text-center text-xs">
                          +{filteredBuildings.length - 7} meer gebouwen
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-neutral-secondary-mid p-3 text-center text-sm">
                      {searchTerm
                        ? "Geen overeenkomende gebouwen gevonden"
                        : "Typ om gebouwen te zoeken"}
                    </div>
                  )}
                </div>
              )}
            </div>

            <StatusFilter
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              options={AVAILABLE_STATUSES}
            />

            <Button
              size="sm"
              className={`${
                currentSection === "buildings"
                  ? "bg-moss-primary text-white shadow-md"
                  : "bg-secondary text-neutral-primary hover:bg-secondary/70"
              } transition-all`}
              onClick={scrollToBuildings}
            >
              Gebouw tabel
            </Button>
            <Button
              size="sm"
              className={`${
                currentSection === "units"
                  ? "bg-moss-primary text-white shadow-md"
                  : "bg-secondary text-neutral-primary hover:bg-secondary/70"
              } transition-all`}
              onClick={scrollToUnits}
            >
              Units tabel
            </Button>
          </div>

          <div className="flex items-center gap-3">
            {currentSection === "buildings" && (
              <div className="flex items-center">
                <Button
                  size="sm"
                  className="bg-moss-primary/10 text-moss-primary border-moss-primary border p-2"
                  onClick={() => setBuildingLabelsExpanded((v) => !v)}
                >
                  <div className="flex items-center gap-1">
                    <Tag size={16} />
                    <span>
                      {selectedBuildingLabels.size} gebouw labels geselecteerd
                    </span>
                  </div>
                </Button>

                {buildingLabelsExpanded && (
                  <div className="ml-2 flex max-w-[500px] flex-wrap gap-2 overflow-x-auto">
                    <div className="mb-1 flex w-full items-center justify-between">
                      <div className="flex gap-2">
                        <Chip
                          onClick={(e) => selectAllBuildingLabels(e)}
                          className="bg-moss-primary cursor-pointer text-white"
                          size="sm"
                        >
                          Selecteer alle
                        </Chip>
                        <Chip
                          onClick={(e) => deselectAllBuildingLabels(e)}
                          className="bg-brick-primary cursor-pointer text-white"
                          size="sm"
                        >
                          Deselecteer alle
                        </Chip>
                      </div>
                      <Button
                        size="sm"
                        variant="light"
                        className="text-neutral-secondary-mid"
                        startContent={<X size={16} />}
                        onClick={() => setBuildingLabelsExpanded(false)}
                      >
                        Minimaliseer weergave
                      </Button>
                    </div>

                    {availableBuildingLabels
                      .slice()
                      .sort((a, b) => a.localeCompare(b, "nl"))
                      .map((label) => (
                        <Chip
                          key={label}
                          onClick={(e) => toggleBuildingLabel(label, e)}
                          className={
                            selectedBuildingLabels.has(label)
                              ? "bg-moss-primary/10 text-moss-primary border-moss-primary cursor-pointer border"
                              : "bg-secondary text-neutral-secondary-mid cursor-pointer"
                          }
                          size="sm"
                        >
                          <div className="flex gap-1">
                            <span>{label}</span>
                            <span className="text-xs opacity-75">
                              ({buildingLabelCounts[label]?.available || 0}/
                              {buildingLabelCounts[label]?.total || 0})
                            </span>
                          </div>
                        </Chip>
                      ))}
                  </div>
                )}
              </div>
            )}

            {currentSection === "units" && (
              <div className="flex items-center">
                <Button
                  size="sm"
                  className="bg-moss-primary/10 text-moss-primary border-moss-primary border p-2"
                  onClick={() => setUnitLabelsExpanded((v) => !v)}
                >
                  <div className="flex items-center gap-1">
                    <Tag size={16} />
                    <span>
                      {selectedUnitLabels.size} unit labels geselecteerd
                    </span>
                  </div>
                </Button>

                {unitLabelsExpanded && (
                  <div className="ml-2 flex max-w-[500px] flex-wrap gap-2 overflow-x-auto">
                    <div className="mb-1 flex w-full items-center justify-between">
                      <div className="flex gap-2">
                        <Chip
                          onClick={(e) => selectAllUnitLabels(e)}
                          className="bg-moss-primary cursor-pointer text-white"
                          size="sm"
                        >
                          Selecteer alle
                        </Chip>
                        <Chip
                          onClick={(e) => deselectAllUnitLabels(e)}
                          className="bg-brick-primary cursor-pointer text-white"
                          size="sm"
                        >
                          Deselecteer alle
                        </Chip>
                      </div>
                      <Button
                        size="sm"
                        variant="light"
                        className="text-neutral-secondary-mid"
                        startContent={<X size={16} />}
                        onClick={() => setUnitLabelsExpanded(false)}
                      >
                        Minimaliseer weergave
                      </Button>
                    </div>

                    {availableUnitLabels
                      .slice()
                      .sort((a, b) => a.localeCompare(b, "nl"))
                      .map((label) => (
                        <Chip
                          key={label}
                          onClick={(e) => toggleUnitLabel(label, e)}
                          className={
                            selectedUnitLabels.has(label)
                              ? "bg-moss-primary/10 text-moss-primary border-moss-primary cursor-pointer border"
                              : "bg-secondary text-neutral-secondary-mid cursor-pointer"
                          }
                          size="sm"
                        >
                          <div className="flex gap-1">
                            <span>{label}</span>
                            <span className="text-xs opacity-75">
                              ({unitLabelCounts[label]?.available || 0}/
                              {unitLabelCounts[label]?.total || 0})
                            </span>
                          </div>
                        </Chip>
                      ))}
                  </div>
                )}
              </div>
            )}

            <Button
              startContent={<Printer size={16} />}
              onClick={onPrint}
              className="bg-neutral-primary hover:bg-neutral-primary/90 text-white shadow-sm transition-all"
              size="sm"
            >
              PDF Export
            </Button>

            <Button
              isLoading={refreshing}
              startContent={<ArrowsClockwise size={16} />}
              onClick={onRefresh}
              className="bg-moss-primary hover:bg-moss-primary/90 text-white shadow-sm transition-all"
              size="sm"
            >
              Vernieuwen
            </Button>
          </div>
        </div>

        {selectedBuildings.size > 0 && (
          <div className="border-neutral-secondary-mid/20 flex flex-wrap items-center gap-2 border-t pt-3">
            <span className="text-neutral-primary text-sm font-semibold">
              Geselecteerde gebouwen:
            </span>
            <Button
              size="sm"
              variant="flat"
              className="text-brick-primary hover:bg-brick-primary/10 transition-colors"
              onClick={onClearAllBuildings}
            >
              Wis alle
            </Button>
            {Array.from(selectedBuildings).map((buildingId) => {
              const building = filteredBuildings.find((b) => b.id === buildingId) || {
                id: buildingId,
                address: buildingId,
              };
              return (
                <Chip
                  key={buildingId}
                  onClose={() => onRemoveBuilding(buildingId)}
                  variant="flat"
                  className="bg-moss-primary/10 border-moss-primary/30 text-moss-primary border shadow-sm"
                  size="sm"
                >
                  {building.id}
                </Chip>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
